import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import CloseIconButton from 'Src/components/buttons/CloseIconButton';
import Copy from 'Src/components/Copy';
import TitleItems from 'Src/components/TitleItems';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { UsedModalSearchParamControl } from 'Src/feature/modal/useModalControl';

import { Field, Form, usePayOut } from './usePayOut';

const sectionId = 'PayOutView';

interface PayOutProps {
  baseModalControl: UsedModalSearchParamControl;
}

const PayOutView: FC<PayOutProps> = ({
  baseModalControl,
}) => {
  const { t } = useTranslation();
  const store = usePayOut(baseModalControl);

  const { form } = store;
  const amount = form.state.watch('amount');

  return (
    <>
      <Modal
        data-testid={sectionId}
        variant="fullScreen"
        open={store.modal.base.isOpen}
      >
        <ModalHeader closeButton={<CloseIconButton onClick={store.onClose} />}>
          <ModalTitle copy={t('payInOut.labels.payOut')} />
        </ModalHeader>
        <ModalContent>
          <Form usedForm={form} omitChangeTracking>
            <Field
              type="currency"
              name="amount"
              label={t('payInOut.labels.payOutAmount')}
              required
            />
            <Field
              data-testid={`${sectionId}-reason-toggle-list`}
              type="toggle"
              name="payInOutReasonId"
              variant="buttons"
              label={t('labels.whatIsItFor')}
              options={store.options.payOutReasons ?? []}
              // show an error message to user when pay out reason(s) not configured or empty
              // - "undefined" value will fall back to the internal validation mechanisms for this field
              error={!store.options.payOutReasons?.length ? true : undefined}
              errorText={!store.options.payOutReasons?.length ? t('payInOut.labels.payOutReasonsNotAvailable') : undefined}
              required
            />
            <Field
              type="text"
              name="notes"
              multiline
              maxRows={3}
              label={t('payInOut.labels.payOutNotes')}
            />
            {store.options.paymentMethodId.length > 1 && (
              <Field
                type="select"
                variant="contained"
                fullWidth
                name="paymentMethodId"
                label={t('payInOut.labels.otherPaymentMethod')}
                options={store.options.paymentMethodId}
              />
            )}
          </Form>
        </ModalContent>
        <ModalActions>
          <ModalAction
            data-testid={`${sectionId}-record-btn`}
            onClick={store.onRecordPayOut}
            color="primary"
            disabled={!store.canSubmit}
            label={t('payInOut.labels.payOutRecord')}
          />
        </ModalActions>
      </Modal>

      <Modal
        data-testid={`${sectionId}-modal-saveConfirm`}
        open={store.modal.done.isOpen}
      >
        <ModalContent flex={{ alignItems: 'center', justifyContent: 'center' }}>
          <Copy value={t('payInOut.labels.payOutConfirm')} />
        </ModalContent>
        <ModalActions>
          <ModalAction onClick={store.onClose} color="primary" label={t('labels.done')} />
        </ModalActions>
      </Modal>

      <Modal
        data-testid={`${sectionId}-confirm-modal`}
        open={store.modal.confirm.isOpen}
        onBackDropClick={store.onClose}
      >
        <ModalHeader closeButton={<CloseIconButton onClick={store.onClose} />}>
          <ModalTitle copy={t('payInOut.labels.payOutConfirmTitle')} />
        </ModalHeader>
        <ModalContent>
          <TitleItems
            variant={['background', 'large']}
            justifyContent="space-between"
            items={[
              { primary: t('employee.label'), secondary: store.employeeFullName },
              { primary: t('payInOut.labels.payOutAmount'), secondary: <Copy type="currency" value={amount} /> }
            ]}
          />
        </ModalContent>
        <ModalActions>
          <ModalAction color="secondary" onClick={store.onClose} label={t('labels.cancel')} />
          <ModalAction color="primary" onClick={store.onSave} label={t('payInOut.labels.confirmPayOut')} />
        </ModalActions>
      </Modal>
    </>
  );
};

export type { PayOutProps };
export { PayOutView, sectionId };
